import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, OnDestroy, AfterViewInit } from '@angular/core';
import { Observable, Subscription, zip } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { SearchType } from '@search-bar/entities/search-type';
import { AppFacade } from '@app/app.facade';
import { Airport } from '@app/modules/core/interfaces/airport';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { AlertPayload, AlertResponse } from '@shared/widgets/entities/alerts';
import { WidgetsFacade } from '@shared/widgets/store/widgets.facade';
import { alertPayloadMapper } from '@shared/widgets/mappers/alert-payload.mapper';
import { SearchBarState } from '@shared/base-search-bar/store/state/search-bar.state';
import { User } from '@app/modules/core/interfaces/user';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { environment } from '@environment';
import { userAlertsRoute } from '@user-alerts/constants/user-alerts-routes.constants';
import { FlightType } from '@search-bar/entities/flight-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { ButtonType } from '@design-system/components/molecules/button/entities/button.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { HeaderWidgetConfig, HeaderWidgetType } from '@design-system/components/templates/header/entities/header.types';
import { CheckboxAlign } from '@design-system/components/organisms/checkbox/entities/checkbox.types';

@Component({
  selector: 'app-create-alert-mobile',
  templateUrl: './create-alert-mobile.component.html',
  styleUrls: ['./create-alert-mobile.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CreateAlertMobileComponent implements OnInit, OnDestroy, AfterViewInit {

  public searchType: SearchType = SearchType.ALERT;
  public priceForm: UntypedFormGroup;
  public priceItemForm: string = 'PRICE';
  public validate: boolean = false;
  public isSubmitted: boolean = false;
  public createAlertModal: boolean = false;
  public isLoadingCreateAlert: boolean = false;
  public createAlertWarning: boolean = false;
  public alertRepeatedAlert: boolean = false;
  public saveCreateAlert: boolean = true;
  public directOnly: boolean = false;
  public flexDates: boolean = false;
  public isMobile: boolean = false;
  public alertCreate: boolean = false;
  public createAlertData: AlertPayload;

  public readonly closedButtonType = ButtonType.Secondary;

  public readonly typographyFontSize = TypographyFontSize;
  public readonly typographyFontFamily = TypographyFontFamily;
  public readonly typographyFontColor = TypographyColor;

  public readonly alignCheckboxes = CheckboxAlign;

  private subscriptions: Subscription[] = [];
  private price: number = 0;
  private calendarRoute: string = 'calendar';
  private alerts: AlertResponse[];
  private initialValues: Readonly<{
    directOnly: boolean,
    flexDates: boolean
  }>;

  constructor(
    private readonly transloco: TranslocoService,
    private appFacade: AppFacade,
    private formValidationService: FormValidationService,
    private route: ActivatedRoute,
    private router: Router,
    private facade: WidgetsFacade,
    private mobileDeviceService: MobileDeviceService,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private widgetsFacade: WidgetsFacade) {
    this.isLoggedIn();
  }

  ngOnInit(): void {
    this.createPriceForm();
    this.initialValues = { directOnly: this.directOnly, flexDates: this.flexDates };
    this.subscriptions.push(
      this.isLoadingCreateAlert$.subscribe(isLoadingCreateAlert => {
        if (this.isLoadingCreateAlert && !isLoadingCreateAlert) {
          this.toggleCreateAlertModal();
        }
        this.isLoadingCreateAlert = isLoadingCreateAlert;
      })
    );
    this.subscriptions.push(
      this.widgetsFacade.alertsEntities$.subscribe(alertsEntities => this.alerts = alertsEntities)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.isLoggedIn$.pipe(take(2)).subscribe(isLoggedIn => {
      if (!isLoggedIn || !this.isMobile) {
        this.redirectToHome();
      }
    }).unsubscribe();
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$.pipe(map(isMobileDevice => isMobileDevice));
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get airports$(): Observable<Airport[]> {
    return this.appFacade.airports$;
  }

  get isLoadingCreateAlert$(): Observable<boolean> {
    return this.facade.createAlert$;
  }

  get searchData$(): Observable<SearchBarState> {
    return this.appFacade.searchData$;
  }

  get headerWidgets(): HeaderWidgetConfig[] {
    return [
      {
        Type: HeaderWidgetType.Title,
        Label: this.transloco.translate('GENERAL_WIDGET_MODULE.ALERTS.CONFIRM.MIN_TITLE'),
        LabelAlign: 'center'
      },
      {
        Type: HeaderWidgetType.Close,
      },
    ];
  }

  get priceValue(): number {
    return this.priceForm?.get(this.priceItemForm)?.value;
  }

  get currencyCode(): string {
    return environment.countryInfo.currency_code;
  }

  private isLoggedIn(): void {
    this.subscriptions.push(
      this.isMobile$.subscribe(x => {
        this.isMobile = x;
      })
    );
  }

  private createPriceForm(): void {
    this.priceForm = new UntypedFormGroup({
      [this.priceItemForm]: new UntypedFormControl(
        this.price, [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(10),
          Validators.max(null),
          Validators.min(1)
        ])
    });
  }

  public showMobileCalendar(type: CalendarField): void {
    const stateCalendar = {
      activeType: type,
      departurePlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.FLIGHT_DEPARTURE',
      returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.FLIGHT_RETURN',
      searchType: SearchType.FLIGHT_RESULTS,
      departureSelectionDisabled: false,
      minMaxCalendarInterface: null,
    };
    this.router.navigate(
      [{ outlets: { detail: [this.calendarRoute] } }],
      { state: stateCalendar, relativeTo: this.route, skipLocationChange: true });

  }

  private formInvalidControls(formValidation: UntypedFormGroup): boolean {
    return this.formValidationService.formInvalidControls(formValidation);
  }

  public createAlert(): void {
    this.validate = true;
    setTimeout(() => {
      this.validate = false;
    }, 1000);

    this.isSubmitted = true;
    if (!this.formInvalidControls(this.priceForm)) {
      zip(
        this.appFacade.user$.pipe(take(1)),
        this.appFacade.searchData$.pipe(take(1))
      ).subscribe(([user, searchData]) => {
        if (searchData?.departurePlace?.code &&
          searchData?.arrivalPlace?.code && (searchData.flightType === FlightType.ONEWAY ?
          !!searchData?.departureDate : !!searchData?.departureDate &&
            !!searchData?.arrivalDate)) {
          this.saveCreateAlert = true;
          this.createAlertData = this.getAlertPayload(user, searchData, this.directOnly,
            this.priceForm.get(this.priceItemForm).value, this.flexDates);
          if (this.validateAlertRepeated(this.createAlertData)) {
            this.toggleAlertRepeated();
          } else {
            this.facade.createAlert(this.createAlertData);
            this.alertCreate = true;
            this.baseSearchBarFacade.clearSearchBarDataAction();
          }

        }
      }).unsubscribe();
    }
  }

  private validateAlertRepeated(alert: AlertPayload): boolean {
    return this.alerts.filter(x => x.arrival === alert.arrival && x.departure === alert.departure &&
      x.inbound_max === alert.inbound_max && x.inbound_min === alert.inbound_min)?.length > 0;

  }
  private getAlertPayload(user: User, searchData: SearchBarState, directOnly: boolean,
    maxPriceAlert: string | number, flexDates: boolean): AlertPayload {
    return alertPayloadMapper(user, searchData, directOnly, maxPriceAlert, flexDates);
  }

  public toggleCreateAlertModal(): void {
    this.createAlertModal = !this.createAlertModal;
  }

  public toggleAlertRepeated(): void {
    this.alertRepeatedAlert = !this.alertRepeatedAlert;
  }

  public toggleCreateAlertWarning(): void {
    this.createAlertWarning = !this.createAlertWarning;
  }

  public setFlexDates(): void {
    this.flexDates = !this.flexDates;
  }

  public setDirectOnly(): void {
    this.directOnly = !this.directOnly;
  }

  public close(): void {
    if (!this.alertCreate) {
      this.toggleCreateAlertWarning();
    } else {
      this.redirectToListAlerts();
    }
  }

  public redirectToHome(): void {
    this.router.navigateByUrl('');
  }

  public redirectToListAlerts(): void {
    this.router.navigateByUrl(userAlertsRoute);
  }
}
